<template>
  <div>
    <a-page-header title="执行记录" />

    <a-card style="margin-top: 16px">
      <a-form layout="inline" :model="searchForm" style="margin-bottom: 16px">
        <a-form-item label="计划ID">
          <a-input-number
            v-model:value="searchForm.plan_id"
            placeholder="计划ID"
            style="width: 150px"
            :min="1"
            allowClear
          />
        </a-form-item>
        <a-form-item label="状态">
          <a-select v-model:value="searchForm.status" style="width: 150px" allowClear>
            <a-select-option value="pending">待执行</a-select-option>
            <a-select-option value="executing">执行中</a-select-option>
            <a-select-option value="success">成功</a-select-option>
            <a-select-option value="failed">失败</a-select-option>
            <a-select-option value="skipped">跳过</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="步骤层级">
          <a-select v-model:value="searchForm.step_level" style="width: 120px" allowClear>
            <a-select-option :value="1">大步骤</a-select-option>
            <a-select-option :value="2">小步骤</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="开始时间">
          <a-range-picker
            v-model:value="dateRange"
            format="YYYY-MM-DD HH:mm:ss"
            show-time
            style="width: 400px"
            @change="handleDateRangeChange"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="handleSearch">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>

      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'step_name'">
            <div>
              <span v-if="record.step_level === 2" style="color: #999; margin-right: 8px">└─</span>
              {{ record.step_name }}
            </div>
          </template>
          <template v-else-if="column.key === 'step_order'">
            <span v-if="record.step_level === 1">{{ record.step_order }}</span>
            <span v-else>{{ record.step_order }}.{{ record.sub_step_order }}</span>
          </template>
          <template v-else-if="column.key === 'step_level'">
            <a-tag :color="record.step_level === 1 ? 'blue' : 'default'">
              {{ record.step_level === 1 ? '大步骤' : '小步骤' }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ getStatusText(record.status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'duration'">
            <span v-if="record.duration">{{ formatDuration(record.duration) }}</span>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'test_case_status'">
            <template v-if="record.has_test_case">
              <a-tag color="green">
                <CheckCircleOutlined /> 已创建
              </a-tag>
            </template>
            <template v-else>
              <a-tag color="default">未创建</a-tag>
            </template>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button type="link" @click="handleView(record)">查看详情</a-button>
            <template v-if="record.has_test_case">
              <a-button type="link" @click="handleViewTestCase(record)">
                <EyeOutlined /> 查看用例
              </a-button>
            </template>
            <template v-else>
              <a-button type="link" @click="handleCreateTestCaseV2(record)">
                <PlusOutlined /> 创建用例
              </a-button>
            </template>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 执行记录详情弹窗 -->
    <a-modal
      v-model:open="detailModalVisible"
      title="执行记录详情"
      width="1000px"
      :footer="null"
    >
      <div v-if="logDetail">
        <a-descriptions :column="2" bordered style="margin-bottom: 16px">
          <a-descriptions-item label="记录ID">{{ logDetail.id }}</a-descriptions-item>
          <a-descriptions-item label="计划ID">{{ logDetail.plan_id }}</a-descriptions-item>
          <a-descriptions-item label="步骤ID">{{ logDetail.step_id }}</a-descriptions-item>
          <a-descriptions-item label="步骤名称">{{ logDetail.step_name }}</a-descriptions-item>
          <a-descriptions-item label="步骤顺序">
            <span v-if="logDetail.step_level === 1">{{ logDetail.step_order }}</span>
            <span v-else>{{ logDetail.step_order }}.{{ logDetail.sub_step_order }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="步骤层级">
            <a-tag :color="logDetail.step_level === 1 ? 'blue' : 'default'">
              {{ logDetail.step_level === 1 ? '大步骤' : '小步骤' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="父步骤ID" v-if="logDetail.parent_step_id">
            {{ logDetail.parent_step_id }}
          </a-descriptions-item>
          <a-descriptions-item label="执行状态">
            <a-tag :color="getStatusColor(logDetail.status)">
              {{ getStatusText(logDetail.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="关联知识数">{{ logDetail.knowledge_count || 0 }}</a-descriptions-item>
          <a-descriptions-item label="开始时间">{{ logDetail.start_time || '-' }}</a-descriptions-item>
          <a-descriptions-item label="结束时间">{{ logDetail.end_time || '-' }}</a-descriptions-item>
          <a-descriptions-item label="执行耗时">
            <span v-if="logDetail.duration">{{ formatDuration(logDetail.duration) }}</span>
            <span v-else>-</span>
          </a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ logDetail.create_time }}</a-descriptions-item>
          <a-descriptions-item label="更新时间">{{ logDetail.update_time }}</a-descriptions-item>
        </a-descriptions>

        <a-collapse style="margin-top: 16px">
          <a-collapse-panel key="knowledge" header="关联知识ID列表" v-if="logDetail.knowledge_ids && logDetail.knowledge_ids.length > 0">
            <a-tag v-for="kid in logDetail.knowledge_ids" :key="kid" style="margin: 4px">
              {{ kid }}
            </a-tag>
          </a-collapse-panel>
          <a-collapse-panel key="executed_knowledge" header="已执行知识ID列表" v-if="logDetail.executed_knowledge_ids && logDetail.executed_knowledge_ids.length > 0">
            <a-tag v-for="kid in logDetail.executed_knowledge_ids" :key="kid" style="margin: 4px">
              {{ kid }}
            </a-tag>
          </a-collapse-panel>
          <a-collapse-panel key="request" header="请求数据" v-if="logDetail.request_data">
            <pre style="max-height: 400px; overflow: auto">{{ JSON.stringify(logDetail.request_data, null, 2) }}</pre>
          </a-collapse-panel>
          <a-collapse-panel key="response" header="响应数据" v-if="logDetail.response_data">
            <pre style="max-height: 400px; overflow: auto">{{ JSON.stringify(logDetail.response_data, null, 2) }}</pre>
          </a-collapse-panel>
          <a-collapse-panel key="error" header="错误信息" v-if="logDetail.error_message">
            <a-alert :message="logDetail.error_message" type="error" />
          </a-collapse-panel>
        </a-collapse>
      </div>
    </a-modal>

  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { DownOutlined, CheckCircleOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { getExecutionLogs, getExecutionLogById } from '../api/executionLogs';
import { createTestCaseFromExecutionLog } from '../api/evalTestCaseV2';

export default {
  name: 'ExecutionLogs',
  components: {
    DownOutlined,
    CheckCircleOutlined,
    EyeOutlined,
    PlusOutlined
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const dataSource = ref([]);
    const dateRange = ref(null);
    const searchForm = reactive({
      plan_id: undefined,
      status: undefined,
      step_level: undefined,
      start_time_from: undefined,
      start_time_to: undefined
    });
    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0
    });

    const detailModalVisible = ref(false);
    const logDetail = ref(null);

    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: '计划ID', dataIndex: 'plan_id', key: 'plan_id', width: 100 },
      { title: '步骤名称', key: 'step_name', width: 250 },
      { title: '步骤顺序', key: 'step_order', width: 100 },
      { title: '步骤层级', key: 'step_level', width: 100 },
      { title: '状态', key: 'status', width: 100 },
      { title: '知识数', dataIndex: 'knowledge_count', key: 'knowledge_count', width: 80 },
      { title: '用例状态', key: 'test_case_status', width: 100 },
      { title: '开始时间', dataIndex: 'start_time', key: 'start_time', width: 180 },
      { title: '执行耗时', key: 'duration', width: 120 },
      { title: '操作', key: 'action', width: 200, fixed: 'right' }
    ];

    const getStatusColor = (status) => {
      const colors = {
        pending: 'orange',
        executing: 'blue',
        success: 'green',
        failed: 'red',
        skipped: 'default'
      };
      return colors[status] || 'default';
    };

    const getStatusText = (status) => {
      const texts = {
        pending: '待执行',
        executing: '执行中',
        success: '成功',
        failed: '失败',
        skipped: '跳过'
      };
      return texts[status] || status;
    };

    const formatDuration = (duration) => {
      if (duration < 1000) {
        return `${duration}ms`;
      } else if (duration < 60000) {
        return `${(duration / 1000).toFixed(2)}s`;
      } else {
        const minutes = Math.floor(duration / 60000);
        const seconds = ((duration % 60000) / 1000).toFixed(2);
        return `${minutes}m ${seconds}s`;
      }
    };

    const loadData = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.current,
          pageSize: pagination.pageSize,
          ...searchForm
        };
        // 移除空值
        Object.keys(params).forEach(key => {
          if (params[key] === undefined || params[key] === null || params[key] === '') {
            delete params[key];
          }
        });
        const res = await getExecutionLogs(params);
        dataSource.value = res.data.items || [];
        pagination.total = res.data.total || 0;
      } catch (error) {
        message.error('加载数据失败');
      } finally {
        loading.value = false;
      }
    };

    const handleDateRangeChange = (dates) => {
      if (dates && dates.length === 2) {
        searchForm.start_time_from = dates[0].format('YYYY-MM-DD HH:mm:ss');
        searchForm.start_time_to = dates[1].format('YYYY-MM-DD HH:mm:ss');
      } else {
        searchForm.start_time_from = undefined;
        searchForm.start_time_to = undefined;
      }
    };

    const handleSearch = () => {
      pagination.current = 1;
      loadData();
    };

    const handleReset = () => {
      searchForm.plan_id = undefined;
      searchForm.status = undefined;
      searchForm.step_level = undefined;
      searchForm.start_time_from = undefined;
      searchForm.start_time_to = undefined;
      dateRange.value = null;
      handleSearch();
    };

    const handleTableChange = (pag) => {
      pagination.current = pag.current;
      pagination.pageSize = pag.pageSize;
      loadData();
    };

    const handleView = async (record) => {
      detailModalVisible.value = true;
      try {
        const res = await getExecutionLogById(record.id);
        logDetail.value = res.data;
      } catch (error) {
        message.error('加载详情失败');
      }
    };

    const handleViewTestCase = (record) => {
      if (record.test_case_id) {
        router.push(`/eval-v2/test-case/${record.test_case_id}`);
      }
    };

    const handleCreateTestCaseV2 = (record) => {
      // 跳转到测试用例管理页面，预填执行日志ID
      router.push({
        path: '/eval-v2/test-cases',
        query: { 
          action: 'create',
          execution_log_id: record.id 
        }
      });
    };

    // V1的加入测试集功能已移除，使用V2的创建测试用例

    onMounted(() => {
      loadData();
    });

    return {
      loading,
      dataSource,
      dateRange,
      searchForm,
      pagination,
      columns,
      detailModalVisible,
      logDetail,
      getStatusColor,
      getStatusText,
      formatDuration,
      handleDateRangeChange,
      handleSearch,
      handleReset,
      handleTableChange,
      handleView,
      handleViewTestCase,
      handleCreateTestCaseV2
    };
  }
};
</script>

<style scoped>
</style>

